<!-- Use the slot for changing the button which opens the dialog -->

<template>
  <v-dialog v-model="dialog" max-width="600">
    <template v-slot:activator="{ on: { click }, attrs }">
      <slot :click="openDialog" :attrs="attrs">
        <div>No Button for AddEditAttendeeDialog provided</div>
      </slot>
    </template>
    <template v-slot:default="dialog">
      <v-card>
        <v-card-title>{{ t$(".title") }}</v-card-title>
        <v-card-text>
          <v-form v-model="valid" ref="form">
            <div class="mb-2 mt-4">
              <AutocompleteFormField
                v-model="selectedAttendee"
                :optionsFetch="fetchAttendees"
                :optionText="getContextName()"
                optionValue="personal_record_id"
                :label="t$('.attendee_selection.label')"
                :placeholder="t$('.attendee_selection.placeholder')"
                :rules="[(v) => !!v || t$('.attendee_selection.error')]"
                :disabled="isEdit"
                :selected-items="selectedItems"
                :return-object="true"
              />
            </div>
            <v-radio-group
              class="mt-0"
              v-model="selectedResponse"
              row
              :label="t$('.response_selection.label')"
              :rules="[(v) => !!v || t$('.response_selection.error')]"
            >
              <v-radio label="Zusage" :value="RsvpReply.Attend"></v-radio>
              <v-radio label="Absage" :value="RsvpReply.Decline"></v-radio>
              <v-radio
                label="Antwort ausstehend"
                :value="RsvpReply.Pending"
              ></v-radio>
            </v-radio-group>
            <div class="mt-4">
              <GuestAmountSelection
                v-if="guestsAllowed"
                v-model="guest_count"
                :disabled="guestButtonDisabled"
              />
              <div v-else>
                Gäste sind bei dieser Veranstaltung nicht gestattet.
              </div>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="justify-space-between">
          <v-tooltip top :disabled="isOutsider" v-if="isEdit">
            <template v-slot:activator="{ attrs, on }">
              <div v-on="on" v-bind="attrs">
                <!-- If the button is disabled the tooltip stops working!!! Weird design by Vuetify 2 -->
                <Button
                  color="warn"
                  :disabled="!isOutsider"
                  btn-label="Löschen"
                  @click="handleDelete"
                  v-if="isEdit"
                  class="mr-2"
                ></Button>
              </div>
            </template>
            <span
              >Einladungen an Gruppenmitglieder können nicht gelöscht
              werden.</span
            >
          </v-tooltip>
          <div>
            <TextButton
              :label="t$('common.cancel')"
              @click="handleCancel"
              class="mr-2"
            ></TextButton>
            <Button :btnLabel="t$('common.save')" @click="handleSave"></Button>
          </div>
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue"
import AutocompleteFormField from "features/common/forms/form-fields/AutocompleteFormField.vue"
import { Member, Rsvp } from "suus-api"
import GuestAmountSelection from "components/Event/GuestAmountSelection.vue"
import TextButton from "components/UI/TextButton.vue"
import Button from "components/UI/Button.vue"
import useTranslate from "features/common/translate/concerns/useTranslate"
import { RsvpReply } from "suus-api"
import {
  getContextNameShortPropertyName,
  ViewContext,
} from "concerns/useContextName"
import { useIsOutsider } from "features/events/attendees/add-edit-attendee/is-outsider"

export interface AddEditAttendee {
  attendee: Rsvp
  response: RsvpReply
  guest_count: number
}

export default defineComponent({
  name: "AddEditAttendeeDialog",
  components: {
    Button,
    TextButton,
    AutocompleteFormField,
    GuestAmountSelection,
  },
  props: {
    attendee: Object as PropType<Rsvp>,
    response: String as PropType<RsvpReply | undefined>,
    guests: { type: Number, default: 0 },
    fetchAttendees: Function as PropType<(query: string) => Promise<Member[]>>,
    guestsAllowed: Boolean,
    selectedItems: Array as PropType<Rsvp[]>,
    viewContext: Object as PropType<ViewContext>,
  },
  computed: {
    RsvpReply() {
      return RsvpReply
    },
    isEdit(): boolean {
      return !!this.attendee
    },
    guestButtonDisabled(): boolean {
      return this.selectedResponse !== RsvpReply.Attend
    },
  },
  data() {
    return {
      guest_count: this.guests,
      selectedAttendee: this.attendee,
      selectedResponse: this.response,
      dialog: false,
      valid: true,
    }
  },
  methods: {
    handleCancel() {
      this.$emit("cancel")
      this.dialog = false
    },
    handleSave() {
      this.form.validate()
      if (!this.valid) return
      this.$emit("save", {
        attendee: this.selectedAttendee,
        response: this.selectedResponse,
        guest_count: this.guest_count ?? 0,
      })
      this.dialog = false
    },
    openDialog() {
      this.dialog = true
      this.isOutsider = useIsOutsider(
        computed(() => this.viewContext),
        computed(() => this.attendee?.personal_record_id)
      ).value

      this.$nextTick(() => {
        this.form.resetValidation()
        this.selectedAttendee = this.attendee
        this.selectedResponse = this.response
        this.guest_count = this.guests
      })
    },
    handleDelete() {
      if (!this.isEdit) return
      this.$emit("delete", this.attendee.personal_record_id)
      this.dialog = false
    },
    getContextName() {
      if (this.viewContext == null || this.isEdit) {
        return "full_name"
      } else return getContextNameShortPropertyName(this.viewContext)
    },
  },
  setup(props) {
    const responseOptions = [
      RsvpReply.Attend,
      RsvpReply.Decline,
      RsvpReply.Pending,
    ]
    const form = ref(null)

    const { t$ } = useTranslate("components.add_attendee_dialog")

    const isOutsider = ref(false)

    const isEdit = computed(() => !!props.attendee)

    return {
      responseOptions,
      form,
      t$,
      getContextNameShortPropertyName,
      isOutsider,
      isEdit,
    }
  },
})
</script>

<style></style>
